
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { orderService } from '../services/orderService';
import { Order, OrderStatus, ReturnRequest } from '../types';
import { ShoppingBag, Eye, CheckCircle, Truck, XCircle, Clock, Search, Filter, Loader2, Package, MoreHorizontal, Check, RotateCcw, AlertTriangle, ShieldCheck, DollarSign } from 'lucide-react';

interface AdminOrdersProps {
  storeId: string;
}

const AdminOrders = ({ storeId }: AdminOrdersProps) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'returns'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<OrderStatus | 'ALL'>('ALL');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = async () => {
    setIsLoading(true);
    const orderData = await orderService.getStoreOrders(storeId);
    const returnData = storage.getReturns(storeId);
    setOrders(orderData);
    setReturns(returnData);
    setIsLoading(false);
  };

  const getStatusStyle = (status: Order['status'] | ReturnRequest['status']) => {
    switch (status) {
      case 'DELIVERED': case 'REFUNDED': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'SHIPPED': case 'APPROVED': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'PROCESSING': case 'PENDING': return 'bg-purple-50 text-purple-600 border-purple-100';
      case 'RETURN_REQUESTED': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'CANCELLED': case 'REJECTED': case 'RETURNED': return 'bg-red-50 text-red-600 border-red-100';
      default: return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const updateStatus = (orderId: string, newStatus: OrderStatus) => {
    const all = storage.getOrders();
    const updated = all.map(o => o.id === orderId ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o);
    storage.saveOrders(updated);
    setOrders(updated.filter(o => o.storeId === storeId));
  };

  const handleReturnAction = async (returnId: string, status: ReturnRequest['status']) => {
    setIsProcessing(true);
    await orderService.processReturn(returnId, status);
    loadData();
    setIsProcessing(false);
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'ALL' || o.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const nextStatusMap: Partial<Record<OrderStatus, OrderStatus>> = {
    'PAID': 'PROCESSING',
    'PROCESSING': 'SHIPPED',
    'SHIPPED': 'DELIVERED'
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Orders & Logistics</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Fulfillment Control Center</p>
        </div>
        <div className="flex gap-2 p-1 bg-white rounded-2xl shadow-sm border border-gray-100">
           <button 
             onClick={() => setActiveTab('all')}
             className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'all' ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-gray-400 hover:text-gray-600'}`}
           >
             Order Log
           </button>
           <button 
             onClick={() => setActiveTab('returns')}
             className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all relative ${activeTab === 'returns' ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-gray-400 hover:text-gray-600'}`}
           >
             Returns & Refunds
             {returns.filter(r => r.status === 'PENDING').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-[8px] flex items-center justify-center animate-pulse border-2 border-white">{returns.filter(r => r.status === 'PENDING').length}</span>
             )}
           </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row gap-6 items-center">
        <div className="relative flex-grow w-full">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
          <input 
            type="text" 
            placeholder={activeTab === 'all' ? "Search by Order ID..." : "Search by Return ID..."}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-6 py-4 bg-gray-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {activeTab === 'all' && (
          <div className="flex gap-3 w-full md:w-auto">
            <select 
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as any)}
              className="bg-gray-50 border-none rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="PAID">Paid</option>
              <option value="PROCESSING">Processing</option>
              <option value="SHIPPED">Shipped</option>
              <option value="DELIVERED">Delivered</option>
              <option value="RETURN_REQUESTED">Return Requested</option>
            </select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-blue-600 h-10 w-10" /></div>
      ) : activeTab === 'all' ? (
        <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 text-[10px] uppercase font-black text-gray-400 tracking-widest border-b border-gray-50">
                <tr>
                  <th className="px-10 py-6">Order Identity</th>
                  <th className="px-10 py-6">Timeline</th>
                  <th className="px-10 py-6">Amount</th>
                  <th className="px-10 py-6">Current Status</th>
                  <th className="px-10 py-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredOrders.map((order) => {
                   const nextStatus = nextStatusMap[order.status];
                   return (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-10 py-8">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-blue-50 rounded-2xl text-blue-600">
                            <ShoppingBag className="h-6 w-6" />
                          </div>
                          <div>
                             <span className="text-sm font-black text-gray-900 block">#{order.id}</span>
                             <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{order.userId}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-10 py-8">
                        <span className="text-xs font-bold text-gray-600">{new Date(order.createdAt).toLocaleDateString()}</span>
                        <span className="text-[10px] text-gray-400 block font-medium">Sync Complete</span>
                      </td>
                      <td className="px-10 py-8">
                        <span className="text-lg font-black text-gray-900">${order.total.toLocaleString()}</span>
                        {order.coinsEarned && <p className="text-[8px] text-emerald-500 font-black uppercase">+{order.coinsEarned} Coins Reward</p>}
                      </td>
                      <td className="px-10 py-8">
                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-10 py-8 text-right">
                        <div className="flex justify-end gap-3">
                          {nextStatus && (
                            <button 
                              onClick={() => updateStatus(order.id, nextStatus)}
                              className="px-5 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
                            >
                              <Check className="h-3 w-3" /> Move to {nextStatus.replace('_', ' ')}
                            </button>
                          )}
                          <button className="p-3 bg-gray-100 text-gray-400 hover:bg-gray-900 hover:text-white rounded-xl transition-all">
                             <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                   );
                })}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-10 py-32 text-center text-gray-400 font-black uppercase tracking-widest text-xs opacity-30">No matching orders found in the log.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-6 animate-in fade-in duration-500">
           {returns.length === 0 ? (
             <div className="py-24 text-center space-y-4 opacity-30 bg-white rounded-[40px] border border-dashed border-gray-200">
                <RotateCcw className="h-16 w-16 mx-auto" />
                <p className="font-black uppercase tracking-widest text-xs">No return requests found.</p>
             </div>
           ) : (
             <div className="grid grid-cols-1 gap-6">
                {returns.map((req) => (
                  <div key={req.id} className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-10">
                     <div className="flex items-center gap-8">
                        <div className={`w-20 h-20 rounded-3xl flex items-center justify-center border-2 ${
                          req.status === 'PENDING' ? 'bg-amber-50 border-amber-100 text-amber-600' :
                          req.status === 'APPROVED' ? 'bg-blue-50 border-blue-100 text-blue-600' :
                          'bg-emerald-50 border-emerald-100 text-emerald-600'
                        }`}>
                           <RotateCcw className="h-10 w-10" />
                        </div>
                        <div className="space-y-2">
                           <div className="flex items-center gap-3">
                              <h4 className="text-xl font-black text-gray-900">Return #{req.id}</h4>
                              <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase border ${getStatusStyle(req.status)}`}>
                                {req.status}
                              </span>
                           </div>
                           <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Origin Order: #{req.orderId}</p>
                           <p className="text-sm text-gray-600 font-medium italic">"Reason: {req.reason.replace('_', ' ')}"</p>
                        </div>
                     </div>

                     <div className="flex flex-col sm:flex-row gap-4">
                        {req.status === 'PENDING' && (
                           <>
                             <button 
                               onClick={() => handleReturnAction(req.id, 'REJECTED')}
                               disabled={isProcessing}
                               className="px-8 py-4 bg-white border-2 border-red-100 text-red-500 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-red-50 transition-all"
                             >
                               Reject Claim
                             </button>
                             <button 
                               onClick={() => handleReturnAction(req.id, 'APPROVED')}
                               disabled={isProcessing}
                               className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all"
                             >
                               Approve & Inspect
                             </button>
                           </>
                        )}
                        {req.status === 'APPROVED' && (
                           <button 
                             onClick={() => handleReturnAction(req.id, 'REFUNDED')}
                             disabled={isProcessing}
                             className="px-10 py-5 bg-emerald-600 text-white rounded-[2rem] font-black text-[10px] uppercase tracking-widest shadow-2xl shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center gap-2"
                           >
                             <DollarSign className="h-4 w-4" /> Issue Full Refund
                           </button>
                        )}
                        {req.status === 'REFUNDED' && (
                          <div className="flex items-center gap-2 text-emerald-600 font-black text-xs uppercase bg-emerald-50 px-6 py-4 rounded-2xl border border-emerald-100">
                             <ShieldCheck className="h-5 w-5" /> Refund Sync'd
                          </div>
                        )}
                     </div>
                  </div>
                ))}
             </div>
           )}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
